import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthError, login, type AuthUser } from './auth';
import {
  AuthField,
  AuthLink,
  AuthNotice,
  AuthShell,
  AuthSubmit,
  PasswordField,
} from './AuthShell';

export default function Login({
  onSignedIn,
  onForgotPassword,
}: {
  onSignedIn: (user: AuthUser) => void;
  onForgotPassword: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      onSignedIn(await login(email, password));
    } catch (err) {
      setError(err instanceof AuthError ? err.message : t('com_ui_leadhr_login_generic_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t('com_ui_leadhr_login_title')} onSubmit={submit}>
      <AuthField
        id="leadhr-email"
        label={t('com_ui_leadhr_login_email')}
        type="email"
        autoFocus
        autoComplete="username"
        value={email}
        onChange={setEmail}
        placeholder={t('com_ui_leadhr_login_email_placeholder')}
        invalid={!!error}
        describedBy={error ? 'leadhr-login-error' : undefined}
      />
      <PasswordField
        id="leadhr-password"
        label={t('com_ui_leadhr_login_password')}
        value={password}
        onChange={setPassword}
        invalid={!!error}
        describedBy={error ? 'leadhr-login-error' : undefined}
      />
      {error ? (
        <AuthNotice id="leadhr-login-error" tone="error">
          {error}
        </AuthNotice>
      ) : null}
      <AuthSubmit
        busy={busy}
        label={t('com_ui_leadhr_login_submit')}
        busyLabel={t('com_ui_leadhr_login_busy')}
      />
      <AuthLink onClick={onForgotPassword}>{t('com_ui_leadhr_login_forgot')}</AuthLink>
    </AuthShell>
  );
}
